import { ProductTypeVariant } from '@/api/ProductTypeVariantApi';
import { CreatorAttribute, CreatorState } from '@/types/creator';
import { UUID } from '@/types/types';
import { WritableDraft } from '@reduxjs/toolkit';

export function getCreatorAtributes(variants: ProductTypeVariant[]) {
	const creatorAttributes: CreatorAttribute[] = [];

	for (const variant of variants) {
		for (const attributeValue of variant.attributeValues) {
			const attribute = creatorAttributes.find(
				(attribute) => attribute.id === attributeValue.attribute.id
			);

			if (
				attribute &&
				attribute.attributeValues.find(
					(value) => value.id === attributeValue.id
				)
			)
				continue;

			const value = {
				...attributeValue,
				enabled: true,
			};

			if (attribute) attribute.attributeValues.push(value);
			else
				creatorAttributes.push({
					...attributeValue.attribute,
					attributeValues: [value],
				});
		}
	}

	creatorAttributes.sort((a, b) => a.listOrder - b.listOrder);
	for (const attribute of creatorAttributes) {
		attribute.attributeValues.sort((a, b) => a.listOrder - b.listOrder);
	}

	return creatorAttributes;
}

export function setAttributeValuesAvailability(
	state: WritableDraft<CreatorState>
) {
	const hasMatchingVariant = (attributeValuesIds: UUID[]) =>
		state.availableProductTypeVariants.some((variant) => {
			const variantAttributeValuesIds = variant.attributeValues.map(
				(attributeValue) => attributeValue.id
			);

			return attributeValuesIds.every((attributeValueId) =>
				variantAttributeValuesIds.includes(attributeValueId)
			);
		});

	for (let index = 1; index < state.creatorAttributes.length; index++) {
		const creatorAttribute = state.creatorAttributes[index];
		const attributesAbove = state.creatorAttributes.slice(0, index);
		const selectedCombinationsAbove = [
			...new Map(
				state.selectedAttributeValues.map((selectedValues) => {
					const attributeValuesIds = attributesAbove
						.map((attribute) => selectedValues[attribute.id]?.id)
						.filter((id): id is UUID => !!id);

					return [attributeValuesIds.join(), attributeValuesIds] as const;
				})
			).values(),
		];

		for (const creatorAttributeValue of creatorAttribute.attributeValues) {
			creatorAttributeValue.enabled = selectedCombinationsAbove.some(
				(attributeValuesIds) =>
					hasMatchingVariant([
						...attributeValuesIds,
						creatorAttributeValue.id,
					])
			);
		}
	}
}

export function unselectNextAttributes(
	attributeId: UUID,
	state: WritableDraft<CreatorState>
) {
	const attributeIndex = state.creatorAttributes.findIndex(
		(attribute) => attribute.id === attributeId
	);

	for (
		let index = attributeIndex + 1;
		index < state.creatorAttributes.length;
		index++
	) {
		const creatorAttribute = state.creatorAttributes[index];

		for (const selectedValues of state.selectedAttributeValues) {
			selectedValues[creatorAttribute.id] = null;
		}
	}
}

export function setMathinchVariants(state: WritableDraft<CreatorState>) {
	let selectedProductTypeVariants = state.selectedAttributeValues.flatMap(
		(selectedValues) => {
			const selectedAttributeValuesIds = Object.values(selectedValues)
				.filter((attributeValue) => attributeValue !== null)
				.map((attributeValue) => attributeValue.id);

			return state.availableProductTypeVariants.filter((variant) =>
				variant.attributeValues.every((attributeValue) =>
					selectedAttributeValuesIds.includes(attributeValue.id)
				)
			);
		}
	);

	selectedProductTypeVariants = [
		...new Map(
			selectedProductTypeVariants.map((variant) => [variant.id, variant])
		).values(),
	];

	state.selectedProductTypeVariants = selectedProductTypeVariants;
}
